/**
 * The wing HUD feature (M6 plan P31, spec §6.2; order `FEATURE_ORDER.wingHud` 535, after the Charter HUD 530). Runs
 * only while the Reach runs (the wing, P25, is a Reach subsystem).
 *
 * - **Radial menu** (`radial.ts`): Y (bindings `keyboard.wingMenu`) or the pad's D-pad ↑ held
 *   `gamepad.wingMenuHoldSeconds` (a tap keeps `toggleFA`, on release; `input/devices.ts`) opens it; the mouse virtual
 *   stick or the right stick highlights an order; releasing gives it through `commandWing` (P25). While it is open the
 *   player's pitch and yaw are zeroed (the stick aims the menu, not the ship) and Esc closes it. The virtual stick is
 *   centred when it opens and closes.
 * - **Status** (`#wing-hud`): one pip per member up to the cap, the command and the formation; shown while the wing has
 *   members or the radial is open. DOM writes only on change (the M1 HUD rule).
 *
 * The edge is read in `beforeTicks` (the loop clears unconsumed edges before `frame`); `beforeTicks` and `frame`
 * allocate nothing except on the open and close events (D-014).
 */
import { FEATURE_ORDER, type FrameInfo, type GameContext, type GameFeature } from '../../app/feature'
import { drainPressed } from '../../input/devices'
import type { PadLike } from '../../input/playerInput'
import { anyKeyHeld, padPressed } from '../../reach/input'
import type { FormationName } from '../../wing/formation'
import { activeWing, commandWing } from '../../wing/subsystem'
import { loadWingConfig, type Wing, type WingCommandName } from '../../wing/wing'
import {
  createRadialInput,
  createRadialState,
  createWingHudSnapshot,
  createWingHudView,
  loadWingHudConfig,
  mountWingHud,
  RADIAL_ITEMS,
  RADIAL_SOURCE,
  stepRadial,
  type RadialState,
  type WingHudConfig,
  type WingHudElements,
  type WingHudView,
} from './radial'
import './hud.css'

/** What the feature reads and calls of the wing (P25's API by default; tests pass fakes). */
export interface WingHudDeps {
  wing(): Wing | null
  command(cmd: WingCommandName, formation?: FormationName): boolean
}

export interface WingHudOptions {
  deps?: WingHudDeps
  config?: WingHudConfig
  /** Builds the elements (default: real DOM under `ctx.hudRoot`); tests pass fakes. */
  mount?: (ctx: GameContext, pips: number) => { els: WingHudElements; host: HTMLElement | null }
}

export interface WingHudFeature extends GameFeature {
  readonly radial: Readonly<RadialState>
  beforeTicks(ctx: GameContext, f: FrameInfo): void
  frame(ctx: GameContext, f: FrameInfo): void
  dispose(): void
}

const DEFAULT_DEPS: WingHudDeps = { wing: activeWing, command: commandWing }

/** The largest wing (the pip count) when the wing is not up yet. */
function largestWing(): number {
  const c = loadWingConfig()
  return Math.max(c.size, c.corvetteSize)
}

function axis(pad: PadLike | null, index: number): number {
  return pad === null ? 0 : (pad.axes[index] ?? 0)
}

export function createWingHudFeature(opts: WingHudOptions = {}): WingHudFeature {
  const deps = opts.deps ?? DEFAULT_DEPS
  const cfg = opts.config ?? loadWingHudConfig()
  const state = createRadialState()
  const inp = createRadialInput()
  const snap = createWingHudSnapshot()
  let view: WingHudView | null = null
  let host: HTMLElement | null = null
  let keys: readonly string[] = []
  let padButton = -1
  let yawAxis = 2
  let pitchAxis = 3
  let cancel = false
  let ui: GameContext['ui'] | null = null

  const onEscape = (): boolean => {
    if (state.source === RADIAL_SOURCE.none) return false
    cancel = true
    return true
  }

  /** The radial opened or closed: centre the virtual stick, and offer Esc to it only while it is open. */
  function opened(ctx: GameContext, isOpen: boolean): void {
    ctx.input.joystick.x = 0
    ctx.input.joystick.y = 0
    const esc = ctx.ui.escape
    const i = esc.lastIndexOf(onEscape)
    if (isOpen && i < 0) esc.push(onEscape)
    else if (!isOpen && i >= 0) esc.splice(i, 1)
  }

  const feature: WingHudFeature = {
    name: 'wingHud',
    order: FEATURE_ORDER.wingHud,
    get radial(): Readonly<RadialState> {
      return state
    },

    init(ctx: GameContext): void {
      if (ctx.reach === null) return
      const b = ctx.data.bindings
      keys = b.keyboard.wingMenu
      padButton = b.gamepad.buttons.toggleFA
      yawAxis = b.gamepad.axes.yaw
      pitchAxis = b.gamepad.axes.pitch
      ui = ctx.ui
      const max = deps.wing()?.max ?? largestWing()
      const mounted = opts.mount?.(ctx, max) ?? mountWingHud(ctx.hudRoot, cfg, max)
      host = mounted.host
      view = createWingHudView(mounted.els)
      // While the radial is open the sticks aim it: the ship holds its heading.
      ctx.controlFilters.push((c) => {
        if (state.source === RADIAL_SOURCE.none) return
        c.pitch = 0
        c.yaw = 0
      })
    },

    beforeTicks(ctx: GameContext, f: FrameInfo): void {
      if (view === null) return
      const input = ctx.input
      const pad = input.pad
      inp.opened = drainPressed(input, 'wingMenu')
      inp.keyHeld = anyKeyHeld(input.keys, keys)
      inp.padHeld = padPressed(pad, padButton)
      inp.mouseX = input.joystick.x
      inp.mouseY = input.joystick.y
      inp.padX = axis(pad, yawAxis)
      // Standard mapping: stick up is negative.
      inp.padY = -axis(pad, pitchAxis)
      inp.cancel = cancel
      cancel = false
      inp.blocked = ctx.ui.modal > 0 || f.held === true || deps.wing() === null
      const wasOpen = state.source !== RADIAL_SOURCE.none
      const picked = stepRadial(state, inp, cfg.selectDeadzone)
      const isOpen = state.source !== RADIAL_SOURCE.none
      if (isOpen !== wasOpen) opened(ctx, isOpen)
      if (picked >= 0) {
        const item = RADIAL_ITEMS[picked]
        deps.command(item.command, item.formation)
      }
    },

    frame(ctx: GameContext, f: FrameInfo): void {
      const v = view
      if (v === null) return
      // The pause closes it without an order (no `beforeTicks` runs while paused).
      if (f.paused && state.source !== RADIAL_SOURCE.none) {
        state.source = RADIAL_SOURCE.none
        state.selected = -1
        opened(ctx, false)
      }
      const wing = deps.wing()
      snap.live = wing !== null
      snap.count = wing?.count ?? 0
      snap.cap = wing?.cap ?? 0
      snap.command = wing?.command ?? 0
      snap.formation = wing?.formation ?? 0
      snap.radialOpen = state.source !== RADIAL_SOURCE.none
      snap.selected = state.selected
      v.sync(snap)
    },

    dispose(): void {
      if (ui !== null) {
        const i = ui.escape.lastIndexOf(onEscape)
        if (i >= 0) ui.escape.splice(i, 1)
      }
      host?.remove()
      host = null
      view = null
      ui = null
      state.source = RADIAL_SOURCE.none
      state.selected = -1
    },
  }
  return feature
}

const feature: WingHudFeature = createWingHudFeature()

export default feature
